import { FlowDefinition, FormDefinition, Options, User } from "./domain";

export interface Application {
  id: string;
  overallStatus: OverallStatus;
  reviewStatus: ReviewStatus | null;
  serialNumber: string;
  submittedAt: string;
  submittedBy: string;
  assigneeId: string;
  formInstance: {
    form: FormDefinition;
    data: Record<string, any>;
  };
  workflowInstance: {
    workflow: FlowDefinition;
    data: Record<string, any>;
  };
  applicantId: number;
  submitterId: number;
  submitter?: User;
  applicant?: User;
  summary?: string | null;
  allowDiscard?: boolean;
  currentNodeDescription: string | null;
  currentWorkflowNodeDescription?: string | null;
  currentApprovals?: ApplicationCurrentApproval[];
  pendingApprovers?: ApplicationPendingApprover[];
  // temporary field for mock data
  comment?: string;
  approvalId: string;
}

export interface ApplicationPendingApprover {
  id: number;
  name: string;
  code?: string | null;
}

export interface ApplicationCurrentApproval {
  nodeKey: string;
  description: string | null;
  approvers: ApplicationPendingApprover[];
}

export type CreateApplicationPayload = Omit<
  Application,
  | "submittedAt"
  | "id"
  | "serialNumber"
  | "approvalId"
  | "applicantId"
  | "submitterId"
  | "currentNodeDescription"
> &
  ({ id: string } | { serialNumber: string }) & {
    applicantId?: number;
    draftId?: string | null;
  };

export interface ApplicationForm {
  bindingId: string;
  displayOrder: number;
  form: {
    id: string;
    name: string;
    revisionId: string;
    description: string;
  };
  workflow: {
    id: string;
    name: string;
    revisionId: string;
    major?: number;
    minor?: number;
    description: string;
  };
}

export interface ApplicationDetailOptions {
  adminView?: boolean;
}

export type RetryErroredApplicationResult = {
  serialNumber: string;
  success: boolean;
  applicationStatus: string;
  message?: string;
};

export enum OverallStatus {
  InProgress = "In Progress",
  Draft = "Draft",
  OnHold = "On hold",
  CompletedApproved = "Completed(Approved)",
  CompletedRejected = "Completed(Rejected)",
  Error = "Error",
  Canceled = "Canceled",
}

export enum ReviewStatus {
  Approved = "Approved",
  Rejected = "Rejected",
  Pending = "Pending",
  NotStarted = "NotStrated",
  Canceled = "Canceled",
  Skipped = "Skipped",
  Tbd = "TBD",
  Error = "Error",
}

// --- Query Option Types ---

export type ApplicationFilter = {
  overallStatus?: OverallStatus;
  reviewStatus?: ReviewStatus;
  approvalStatus?: (
    | "PENDING"
    | "APPROVED"
    | "REJECTED"
    | "WAITING"
    | "CANCELLED"
    | "TBD"
    | "ERROR"
  )[];
  serialNumber?: string;
  applicantId?: number;
  assigneeId?: string;
  formName?: string;
  workflowName?: string;
  summary?: string;
  formTagIds?: number[];
  workflowTagIds?: number[];
};

export type ApplicationSorter = {
  submittedAt?: "asc" | "desc";
  sortOrder?: "asc" | "desc";
  sortBy?: "created_at" | "applied_at" | "updated_at";
};

export type ApplicationListContextFilter =
  | "submitted"
  | "approving"
  | "shared"
  | "all"
  | "visible";

export interface ApplicationOptions extends Options<Application> {
  type: "application" | "approval";
  listFilter?: ApplicationListContextFilter;
  filter?: ApplicationFilter;
  sorter?: ApplicationSorter;
}

export interface ApplicationFormFilter {
  formName?: string;
  workflowName?: string;
  formTagIds?: number[];
  workflowTagIds?: number[];
}

export interface ApplicationFormSorter {
  sortOrder?: "asc" | "desc";
}

export interface ApplicationFormOptions {
  filter?: ApplicationFormFilter;
  sorter?: ApplicationFormSorter;
  pageSize?: number;
  page?: number;
}

// --- Progress Types ---

export enum ProgressStatus {
  Approved = "approved",
  Pending = "pending",
  NotStarted = "not-started",
  Tbd = "tbd",
  Rejected = "rejected",
  Canceled = "canceled",
}

export enum ProgressType {
  Review = "review",
  Group = "group",
  Condition = "condition",
  End = "end",
}

export type ProgressEventPhase =
  | "nodeEnter"
  | "nodeLeave"
  | "taskEnter"
  | "taskLeave";

export type ProgressEventError = {
  id: string;
  eventId: string;
  name: string;
  status: string;
  phase: ProgressEventPhase;
};

export type ReviewerData = {
  id: string;
  title?: string;
  type: "user" | "department";
  assigneeName: string;
  status: ReviewStatus | null;
  comment?: string;
  timestamp?: string;
  eventErrors?: ProgressEventError[];
};

export interface StepBase {
  id: string;
  title: string;
  status: ProgressStatus;
  errorMessage?: string;
  eventErrors?: ProgressEventError[];
}

export interface ReviewStep extends StepBase {
  type: ProgressType.Review;
  children: ReviewerData[];
  isReportingLine: boolean;
  requiresAllApprovals: boolean;
}

export interface GroupStep extends StepBase {
  type: ProgressType.Group;
  method: "AND" | "OR";
  children: {
    title?: string;
    method: "AND" | "OR";
    data: ReviewerData[];
    isReportingLine: boolean;
    eventErrors?: ProgressEventError[];
  }[];
}

export interface ConditionStep extends StepBase {
  type: ProgressType.Condition;
  children: Progress[];
}

export interface EndStep extends StepBase {
  type: ProgressType.End;
  title: "Completed";
  children: null;
}

export type Progress = (GroupStep | ReviewStep | ConditionStep | EndStep)[];

export interface Comment {
  approvalId: string;
  author: User;
  content: string;
  createdAt: string;
  updatedAt: string;
}
